import withUser from '@/withUser';
import { NextPageContextWithRedux } from '@/types/store/NextPageContextWithRedux';
import {
  deletePublication,
  fetchPublicationDetail,
  viewedPublication,
} from '@/api/publication';
import handlePublicationDetailResult from '@/handlePublicationDetailResult';
import FrontLayout from '@layouts/FrontLayout';
import Breadcrumb, { BreadcrumbItem } from '@components/app/Breadcrumb';
import Header, { NavCategory, NavSubCategory } from '@components/Header';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Link,
  SkipNavContent,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import BgMasqueHaut from '@/components/presentational/img/v2024/BgMasqueHaut.svg';
import UIWidth from '@components/presentational/UIWidth';
import { PiCaretLeftThin, PiFlagThin } from 'react-icons/pi';
import ContentTopDetails from '@components/presentational/ContentTopDetails';
import RessourceDetailsContentArea from '@components/presentational/RessourceDetailsContentArea';
import PublicationIdentityCard from '@components/presentational/Cards/Parts/PublicationIdentityCard';
import VolDeMouettesCommunautes from '@/components/presentational/img/v2024/VolDeMouettesCommunautes.svg';
import Date from '@components/app/Date';
import { RichText } from '@components/presentational/RichText';
import AuthorCard from '@components/app/cards/AuthorCard';
import newsTranslations from '@/translations/news.fr.yml';
import { News } from '@/types/core/News';
import PublicationMediasCard from '@components/community/PublicationMediasCard';
import LikePublicationButton from '@components/app/LikePublicationButton';
import ModalShare from '@components/course/details/ModalShare';
import getConfig from 'next/config';
import { useSelector } from 'react-redux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import PublishNewsModal from '@components/community/PublishNewsModal';
import LoginModal from '@components/LoginModal';
import CommentsCard from '@components/CommentsCard';
import useCanEditPublication, {
  isPublicationCountryOrganiser,
} from '@/hooks/publication';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { CheckAuth } from '@components/app/CheckAuth';
import AdminEditComment from '@components/app/comment/AdminEditComment';
import ButtonWithConfirmTooltip from '@components/app/ButtonWithConfirmTooltip';
import Metadata from '@components/app/Metadata';
import ReportPublicationModal from '@components/publication/ReportPublicationModal';
import { onClickFeaturePublication } from '@components/publication/PublicationCardMenu';
import dayjs from 'dayjs';
import LastUpdate from '@components/app/LastUpdate';
import NotFound from '@/components/app/NotFound';

function PageInfoDetails({ publication }: { publication: News | null }) {
  if (!publication) {
    return <NotFound />;
  }

  const canEdit = useCanEditPublication(publication);
  const currentUser = useSelector(
    (state: ReduxRootState) => state.authentication.user
  );

  const {
    isOpen: isShareModalOpen,
    onOpen: onShareModalOpen,
    onClose: onShareModalClose,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isLoginModalOpen,
    onOpen: onLoginModalOpen,
    onClose: onLoginModalClose,
  } = useDisclosure();
  const {
    isOpen: isReportModalOpen,
    onOpen: onReportModalOpen,
    onClose: onReportModalClose,
  } = useDisclosure();

  const host = getConfig().publicRuntimeConfig.HOST;
  const toast = useToast();
  const { query, push } = useRouter();

  const [displayedPublication, setDisplayedPublication] = useState(publication);
  const featuredDate = useMemo(() => {
    return displayedPublication.featuredAt
      ? dayjs(displayedPublication.featuredAt).format('DD MMMM YYYY HH:mm')
      : '';
  }, [displayedPublication]);

  const translatedNewsTypes = useMemo(() => {
    return displayedPublication.newsTypes.map(
      newsType => newsTranslations.news_type[newsType]
    );
  }, [displayedPublication.newsTypes]);

  const removePublication = async () => {
    let response: Response;
    try {
      response = await deletePublication(
        displayedPublication.id,
        displayedPublication.__TYPE__
      );
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    if (response.ok) {
      toast({
        title: 'Succès',
        description: 'La publication a bien été supprimée',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      push('/communautes');
    } else {
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la suppression',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    setDisplayedPublication(publication);
    if (query?.action === 'edit') {
      onEditModalOpen();
    }
  }, [publication.id]);

  return (
    <FrontLayout
      gtmPage={{
        page_type: 'Communauté',
        page_level1: 'Informations',
        page_level2: displayedPublication.title,
      }}
    >
      <Metadata
        title={displayedPublication.title}
        news={displayedPublication}
      />
      <Header
        mainNavItemActive={NavCategory.Communities}
        subNavItemActive={NavSubCategory.MY_COMMUNITY}
      />
      <Breadcrumb
        breadcrumbItems={[
          { label: 'Accueil', href: '/' },
          { label: 'Informations', href: '/communautes' },
          { label: displayedPublication.title },
        ]}
      >
        <BreadcrumbItem label={'Accueil'} href={'/'} />
        <BreadcrumbItem label={'Informations'} href={'/communautes'} />
        <BreadcrumbItem label={displayedPublication.title} />
      </Breadcrumb>
      <SkipNavContent />
      <Box
        as="section"
        backgroundImage={`url("${BgMasqueHaut.src}")`}
        backgroundRepeat="no-repeat"
        backgroundPosition={{ base: '0 -35rem', md: '0 -20rem' }}
        backgroundSize="512rem auto"
        backgroundColor={{ md: 'tournesol.25' }}
        pb={{ base: '1rem', md: '1.25rem' }}
      >
        <Box
          backgroundImage={`url("${VolDeMouettesCommunautes.src}")`}
          backgroundSize="100% auto"
          backgroundRepeat="no-repeat"
          pb={{ base: '1rem', md: '1.25rem' }}
        >
          <UIWidth backgroundColor="white" borderRadius="0 0 1.875rem 0">
            <Flex mb="1rem" justify="space-between" align="center">
              <NextLink href={'/communautes'} passHref>
                <Link display="inline-block">
                  <Box
                    as="span"
                    display="inline-block"
                    position="relative"
                    bottom="-.125em"
                  >
                    <Icon as={PiCaretLeftThin} boxSize="1.125em" />
                  </Box>
                  Retour
                </Link>
              </NextLink>
              {currentUser &&
                currentUser.id !== displayedPublication.author?.id && (
                  <Flex py={'0.5rem'} justify={'flex-end'}>
                    <Button
                      variant="link"
                      display="flex"
                      gap="0.25rem"
                      color="black.75"
                      textDecoration="none"
                      _hover={{ '&>svg': { color: 'black.100' } }}
                      onClick={onReportModalOpen}
                      title={
                        displayedPublication.isReported
                          ? 'Vous avez déjà signalé cette publication'
                          : ''
                      }
                      isDisabled={displayedPublication.isReported}
                    >
                      <Icon as={PiFlagThin} boxSize="1.25rem" />
                      <Text fontSize="0.75rem">Signaler</Text>
                    </Button>
                  </Flex>
                )}
            </Flex>
            <Box as="article" pb={{ base: '1rem', md: '2rem' }}>
              <Heading
                as="h1"
                variant="h1"
                mb="1em"
                lineHeight={{ base: 1, md: 1.2 }}
              >
                {displayedPublication.title}
              </Heading>
              <ContentTopDetails
                avatar={
                  displayedPublication.author?.avatar?.filename
                    ? `/data/avatars/${displayedPublication.author.avatar?.filename}`
                    : null
                }
                author={
                  displayedPublication.author
                    ? displayedPublication.author.displayName
                    : 'Anonyme'
                }
                lastUpdateString={
                  <LastUpdate
                    firstUpdate={displayedPublication.publishedAt}
                    lastUpdate={displayedPublication.updatedAt}
                  />
                }
                likes={displayedPublication.countSelections}
                shares={displayedPublication.countRecommendations}
                subtitle={displayedPublication.abstract}
                editionButton={
                  <>
                    {isPublicationCountryOrganiser(
                      displayedPublication,
                      currentUser
                    ) && (
                      <Button
                        variant="tournesol"
                        w="fit-content"
                        size="xs"
                        alignSelf={['end', 'revert']}
                        title={
                          displayedPublication.featured
                            ? `Mise à la Une le ${featuredDate}`
                            : undefined
                        }
                        onClick={() => {
                          onClickFeaturePublication(
                            displayedPublication,
                            toast,
                            async () => {
                              const refreshedPublication = await fetchPublicationDetail(
                                displayedPublication.id
                              );
                              setDisplayedPublication(
                                refreshedPublication as News
                              );
                            }
                          );
                        }}
                      >
                        {displayedPublication.featured
                          ? `Ne plus mettre à la Une${
                              displayedPublication.country
                                ? ' de mon pays'
                                : ' monde'
                            }`
                          : `Mettre à la Une${
                              displayedPublication.country
                                ? ' de mon pays'
                                : ' monde'
                            }`}
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button
                          variant="tournesol"
                          w="fit-content"
                          size="xs"
                          alignSelf={['end', 'revert']}
                          onClick={onEditModalOpen}
                        >
                          Modifier
                        </Button>
                        <ButtonWithConfirmTooltip
                          variant="tournesol"
                          confirmButtonVariant={'tournesol'}
                          confirmMessage={
                            'Êtes-vous sûr de vouloir supprimer cette info ?'
                          }
                          w="fit-content"
                          size="xs"
                          alignSelf={['end', 'revert']}
                          onClickConfirm={removePublication}
                        >
                          Supprimer
                        </ButtonWithConfirmTooltip>
                      </>
                    )}
                  </>
                }
              />
              <Grid
                templateColumns={{ base: '1fr', sm: '2fr 1fr' }}
                gap={{ base: '1rem', md: '2.5rem' }}
              >
                <RessourceDetailsContentArea
                  imageUrl={
                    displayedPublication.image?.filename
                      ? `/data/event/images/${displayedPublication.image.filename}`
                      : ''
                  }
                  imageAlt={displayedPublication.imageAlt}
                >
                  <Box>
                    <RichText html={{ __html: displayedPublication.text }} />
                  </Box>
                  <Flex
                    justify={['center', 'end']}
                    wrap="wrap"
                    gap={{ base: '0.5rem', sm: '1rem' }}
                    width="100%"
                  >
                    <LikePublicationButton
                      publication={displayedPublication}
                      onChange={() => {
                        setDisplayedPublication({
                          ...displayedPublication,
                          countSelections:
                            displayedPublication.countSelections +
                            (displayedPublication.isSelected ? -1 : 1),
                          isSelected: !displayedPublication.isSelected,
                        });
                      }}
                      variant="sunflower"
                    />
                    <CheckAuth
                      actionId="share"
                      actionAfterLogin={onShareModalOpen}
                      accentColor="tournesol"
                    >
                      {onClick => (
                        <Button
                          size={['xxs', 'sm']}
                          variant="tournesolOutline"
                          onClick={onClick}
                        >
                          Partager
                        </Button>
                      )}
                    </CheckAuth>
                  </Flex>
                </RessourceDetailsContentArea>
                <GridItem>
                  <VStack
                    className="article-sidebar"
                    align="stretch"
                    spacing={0}
                  >
                    <PublicationIdentityCard
                      accentColor={'tournesol.100'}
                      title="L'info en bref"
                      newsTypes={translatedNewsTypes}
                    />
                    <PublicationMediasCard
                      variant={'tournesol'}
                      accentColor={'tournesol.100'}
                      publication={displayedPublication}
                      onClickLogin={onLoginModalOpen}
                    />
                  </VStack>
                </GridItem>
                <GridItem gridArea={{ sm: '2 / 1 / 3 / 3', md: 'revert' }}>
                  <Grid
                    templateColumns={{ base: '1fr', sm: '2fr 1fr', md: '1fr' }}
                    gap={{ sm: '1rem', md: 0 }}
                  >
                    <GridItem>
                      <AuthorCard
                        user={displayedPublication.author}
                        accentColor="tournesol.100"
                        buttonVariant="tournesolOutline"
                        height="revert"
                        showColleagueRelationship={
                          !!displayedPublication.author
                        }
                      />
                    </GridItem>
                  </Grid>
                </GridItem>
                <GridItem gridArea={['5 / 1 / 6 / 2', '', '3 / 1 / 4 / 3']}>
                  <CommentsCard
                    accentColor="tournesol.100"
                    publication={displayedPublication}
                    onChange={async () => {
                      const refreshedPublication = await fetchPublicationDetail(
                        displayedPublication.id
                      );
                      setDisplayedPublication(refreshedPublication as News);
                    }}
                  />
                </GridItem>
              </Grid>
            </Box>
          </UIWidth>
        </Box>
      </Box>
      <AdminEditComment
        onSubmitEnd={async () => {
          const news = (await fetchPublicationDetail(
            publication.id,
            currentUser
          )) as News;
          setDisplayedPublication(news);
        }}
      />
      {isShareModalOpen && (
        <ModalShare
          title="Partager la publication"
          isPublicationRecommendation
          submitButtonLabel="Partager la publication"
          apiEndpoint={`/api/publications/${displayedPublication.id}/recommend`}
          url={`${host}/info/${displayedPublication.slug}/${displayedPublication.id}`}
          isOpen={isShareModalOpen}
          onClose={onShareModalClose}
          variant="sunflower"
          onSubmitEnd={() => {
            setDisplayedPublication({
              ...displayedPublication,
              countRecommendations:
                displayedPublication.countRecommendations + 1,
            });
            toast({
              title: 'Succès',
              description: 'La publication a bien été partagée',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            onShareModalClose();
          }}
        />
      )}
      {isEditModalOpen && (
        <PublishNewsModal
          isOpen={isEditModalOpen}
          onClose={onEditModalClose}
          onSubmitEnd={async () => {
            const news = (await fetchPublicationDetail(
              publication.id,
              currentUser
            )) as News;
            setDisplayedPublication(news);
            onEditModalClose();
          }}
          news={displayedPublication}
        />
      )}
      {isLoginModalOpen && (
        <LoginModal
          onClose={onLoginModalClose}
          onLoginSuccess={user => {
            toast({
              title: `Bienvenue ${user?.displayName}`,
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }}
        />
      )}
      {isReportModalOpen && (
        <ReportPublicationModal
          isOpen={isReportModalOpen}
          onClose={onReportModalClose}
          publication={displayedPublication}
          onSubmitEnd={async () => {
            const refreshedPublication = await fetchPublicationDetail(
              displayedPublication.id
            );
            toast({
              title: 'Succès',
              description: 'La publication a bien été signalée',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            setDisplayedPublication(refreshedPublication as News);
            onReportModalClose();
          }}
        />
      )}
    </FrontLayout>
  );
}

PageInfoDetails.getInitialProps = async (context: NextPageContextWithRedux) => {
  const currentUser = context.reduxStore.getState().authentication.user;

  const news = await fetchPublicationDetail(
    context.query.publication as string,
    currentUser,
    { context }
  );

  if (currentUser && news) {
    viewedPublication(news.id, { context });
  }

  return handlePublicationDetailResult(context, news);
};
export default withUser(PageInfoDetails);
